import * as members from "privates/privateMembers";
import * as postMessage from "privates/postMessage";
import { validate } from "privates/validations/validation";
import {formatPlatformizedHttpError} from 'privates/validations/errors';

/**
 * @namespace wix-crm
 * @summary The wix-crm module contains functionality for working with
 *  [your site's contacts](https://support.wix.com/en/article/about-your-contact-list)
 *  from client-side code.
 * @description
 *  To use the CRM API, import `wixCRM` from the `wix-crm` module:
 *
 *    ``` javascript
 *    import wixCRM from 'wix-crm';
 *    ```
 */

/**
 * @typedef {external:Object} wix-crm~ContactInfo
 * @summary An object that contains information about a site contact.
 * @property {external:String} firstName Contact's first name.
 * @property {external:String} lastName Contact's last name.
 * @property {external:String} picture Contact's image source.
 * @property {external:String[]} emails List of contact's email addresses.
 * @property {external:String} loginEmail Email address the contact who is also
 *  a member uses to log into the system.
 * @property {external:String[]} phones List of contact's phone numbers.
 * @property {external:String[]} labels List of contact's labels. [Labels](https://support.wix.com/en/article/creating-contact-labels)
 *  are used to organize contacts. When setting the `labels` property, you can
 *  only list labels that already exist in your site's [Contacts List](https://support.wix.com/en/article/accessing-your-contact-list).
 * @property {external:String} language Contact's language.
 * @property {external:String|external:Number|external:Date} customFields Any
 *  number of custom fields. [Customs fields](https://support.wix.com/en/article/adding-custom-fields-to-contacts)
 *  are used to store additional information about your site's contacts. When
 *  setting a custom field, use key:value pairs where the key matches the names
 *  defined in your site's [Contacts List](https://support.wix.com/en/article/accessing-your-contact-list).
 *  You can only set values for custom fields that already exist in the Contacts
 *  application.
 * @snippet [CRM-createContact_custom-fields.es6=Create a new contact with custom fields]
 * @memberof wix-crm
 */



class CRM {
  constructor(appDefId) {
    members._appDefId.set(this, appDefId);
  }

  /**
   * @function createContact
   * @syntax
   * function createContact(contactInfo: ContactInfo): Promise&lt;String&gt;
   * @summary Creates a new contact or updates an existing contact.
   * @description
   *  The `createContact()` function returns a Promise that resolves to the newly
   *  created or updated contact's ID when the contact has been created or updated.
   *
   *  The passed [`ContactInfo`](#ContactInfo) object must contain at least one
   *  email address or phone number.
   *
   *  Calling `createContact()` performs one of the following. (The contact information
   *  specified in the `contactInfo` parameter matches an existing contact if it
   *  contains an email address or phone number from an existing contact.)
   *  + If there is no matching existing contact, a new contact is created using the
   *    information specified using the `contactInfo` parameter.
   *  + If there is a matching existing contact, it is updated with the information
   *    specified using the `contactInfo` parameter. Any existing contact information
   *    that is not explicity overriden in the `contactInfo` parameter retains its
   *    existing value.
   * @snippet [CRM-createContact.es6=Create a new contact]
   * @snippet [CRM-createContact_custom-fields.es6=Create a new contact with custom fields]
   * @snippet [CRM-createContact_emailContact.es6=Create a contact and then send a Triggered Email to the new contact]
   * @param {wix-crm~ContactInfo} contactInfo The information for the contact being created or updated.
   * @returns {Promise}
   * @fulfill {external:String} The ID of the new or updated contact.
   * @reject {external:String} Error message.
   * @memberof wix-crm
   */
  createContact(contactInfo) {
    return new Promise((resolve, reject) => {
      if (!validate({
        compType: "CRM",
        functionName: "createContact",
        dataToValidate: { contactInfo }
      })) {
        return;
      }
      postMessage.sendMessage(
        postMessage.Intent.WIX_CODE_SITE_API,
        postMessage.MessageTypes.CRM_CREATE_CONTACT,
        { contactInfo },
        (data, error) => {
          if (error) {
            reject(formatPlatformizedHttpError(error));
          } else {
            resolve(data.contact.id);
          }
        }
      );
    });
  }

    /**
   * @function emailContact
   * @syntax
   * function emailContact(emailId: String, toContact: string, [options: TriggeredEmailOptions]): Promise&lt;void&gt;
   * @summary Sends a Triggered Email to the contact.
   * @description
   *  To learn more about Triggered Emails, see:
   *  + [About Triggered Emails](https://support.wix.com/en/article/about-triggered-emails)
   *  + [Creating a Triggered Email](https://support.wix.com/en/article/creating-a-triggered-email)
   *  + [How to Send a Triggered Email to Contacts with Code](https://support.wix.com/en/article/how-to-send-a-triggered-email-to-contacts-with-code)
   *
   *
   *  Before using the `emailContact()` function, you need to set up at least one
   *  Triggered Email.
   *
   *  Specify which email to send by passing the email's ID in the `emailId` parameter.
   *
   *  Specify which contact the email is sent to by passing the contact's ID in the `toContact`
   *  parameter.
   *
   *  If the specified Triggered Email contains [variables](https://support.wix.com/en/article/using-variables-to-add-personalized-text),
   *  you can pass values for those variables using the optional `options` parameter.
   *  You pass a [`TriggeredEmailOptions`](#TriggeredEmailOptions) object
   *  which contains the values to be inserted into your email in place of the variables
   *  defined in that email. The values passed must be strings. If the object you
   *  pass to the `options` parameter does not contain a `key:value` pair for a
   *  variable in your Triggered Email, the fallback value defined when creating
   *  your Triggered Email is inserted in place of the variable.
   *
   *  Note that Triggered Emails generates a code snippet for each of your email
   *  templates. The generated code includes the email's ID and keys for all the
   *  email's variable names. You can copy and paste the snippet into your code.
   *  Then, you need to define values for the `toContact` property and for each
   *  variable key. To learn how to use the generated snippet in your code, see
   *  [How to Send a Triggered Email with Code](https://support.wix.com/en/article/how-to-send-a-triggered-email-with-code).
   * @snippet [CRM-emailContact.es6=Send a Triggered Email to contact]
   * @snippet [CRM-emailContact_variables.es6=Send a Triggered Email with variable values]
   * @snippet [CRM-createContact_emailContact.es6=Create a contact and then send a Triggered Email to the new contact]
   * @param {external:String} emailId The Email ID of the Triggered Email to send.
   * @param {external:String} toContact The Contact ID.
   * @param {wix-users~TriggeredEmailOptions} [options] Variable values to insert into the email.
   * @returns {Promise}
   * @fulfill {void} When the email is sent.
   * @reject {external:String} Error message.
   * @memberof wix-crm
   */
  emailContact (emailId, toContact, options) {
    return new Promise((resolve, reject) => {
        if (!validate({
            compType: 'CRM',
            functionName: 'emailContact',
            dataToValidate: {emailId, userId: toContact, emailOptions: options}
        })) {
            reject();
            return;
        }
        postMessage.sendMessage(postMessage.Intent.WIX_CODE_SITE_API,
            'sendUserEmail',
            {emailId, contactId: toContact, options},
            (data, error) => {
                if (error) {
                    reject(formatPlatformizedHttpError(error));
                } else {
                    resolve();
                }
            });
    });
}
}

export default CRM;



// WEBPACK FOOTER //
// ./js/modules/namespaces/crm.es6